/* Opera Depot wishlist. Loaded globally from theme.liquid (not from a
   section) because script tags inside SPA-swapped content never execute —
   the same reason od-sale-display.js and od-resume-row.js are global.

   Replaces the theme-owned Swym UI (PDP heart, header badge feed, wishlist
   page). The Swym app embed stays installed until data migration completes,
   but nothing here talks to it.

   State lives in Alpine.store('wishlist') so the header badge
   (sections/header.liquid reads $store.wishlist.count) and the PDP button
   bindings stay reactive:

     { count, items, ready, loggedIn,
       has(variantId), toggle(item), add(item), remove(variantId), refresh() }

   Logged-in customers: the App Proxy API (/apps/library/wishlist) is the
   source of truth; add/remove update local state optimistically and roll
   back on failure. Guests: localStorage (od-wishlist:guest:v1), no server
   calls. On login, guest items are merged (action:"merge") and the guest
   key is cleared only after the merge succeeds.

   Every mutation dispatches `od:wishlist:changed` on document.

   The wishlist page (sections/od-wishlist-page.liquid tags its root with
   data-od-wishlist-page) is also rendered from here: current price and
   availability come from /products/{handle}.js, and prices are emitted in
   _price-element-shaped markup (data-od-sale-base + .text-primary-600) so
   od-sale-display.js's MutationObserver decorates sale prices on its own. */
(function () {
  "use strict";

  var API = "/apps/library/wishlist";
  var GUEST_KEY = "od-wishlist:guest:v1";
  var MAX_BATCH = 100;

  var warned = false;
  function warnOnce(context, error) {
    if (warned) return;
    warned = true;
    try {
      console.warn("[od-wishlist] " + context, error || "");
    } catch (e) {
      /* console unavailable */
    }
  }

  function isLoggedIn() {
    try {
      var access = window.ODMembershipAccess;
      if (access && access.state && access.state.loggedIn) return true;
    } catch (e) {
      /* fall through */
    }
    try {
      return !!(window.Shopify && window.Shopify.customer && window.Shopify.customer.id);
    } catch (e) {
      return false;
    }
  }

  function normalizeItem(raw) {
    if (!raw || raw.variantId == null) return null;
    return {
      variantId: String(raw.variantId),
      productId: raw.productId != null ? String(raw.productId) : "",
      productHandle: raw.productHandle || "",
      productTitle: raw.productTitle || "",
      variantTitle: raw.variantTitle || "",
      sku: raw.sku || "",
      priceCentsAtAdd: isFinite(Number(raw.priceCentsAtAdd)) ? Number(raw.priceCentsAtAdd) : 0,
      imageUrl: raw.imageUrl || "",
      addedAt: raw.addedAt || new Date().toISOString(),
      // Server rows for fulfilled "notify" requests carry notifiedAt (ISO,
      // stamped when the availability email went out). Optional pass-through
      // for the release banner; guests never receive it (no emails). POSTs
      // that echo it back are harmless — the API whitelist ignores it.
      notifiedAt: typeof raw.notifiedAt === "string" ? raw.notifiedAt : "",
      // "notify" rows are coming-soon notification requests; the page
      // renderer needs the flag as a fallback when the product record
      // cannot be fetched (classification is otherwise by live data).
      itemType: raw.itemType === "notify" ? "notify" : "wishlist",
      source: raw.source || "storefront"
    };
  }

  function readGuest() {
    try {
      var raw = localStorage.getItem(GUEST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      var list = parsed && Array.isArray(parsed.items) ? parsed.items : [];
      return list.map(normalizeItem).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function writeGuest(items) {
    try {
      localStorage.setItem(GUEST_KEY, JSON.stringify({ v: 1, items: items }));
    } catch (e) {
      /* private mode etc. — state still lives for this page view */
    }
  }

  function clearGuest() {
    try {
      localStorage.removeItem(GUEST_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function apiGet() {
    return fetch(API, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then(function (response) {
      return response.json().then(function (payload) {
        return { status: response.status, payload: payload };
      });
    });
  }

  function apiPost(body) {
    return fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (payload) {
        if (!response.ok || payload.ok === false) {
          throw new Error("wishlist API " + response.status);
        }
        return payload;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Alpine store                                                        */
  /* ------------------------------------------------------------------ */

  var storeDef = {
    count: 0,
    items: [],
    ready: false,
    loggedIn: false,

    // Alpine calls init() when the store registers.
    init() {
      this.loggedIn = isLoggedIn();
      var self = this;
      if (!this.loggedIn) {
        this._commit(readGuest(), false);
        this.ready = true;
        this._notify();
        return;
      }
      // Merge-on-login: push any guest items to the server first, clear
      // the guest key only when every batch succeeded, then load.
      var guestItems = readGuest();
      var merge = Promise.resolve();
      if (guestItems.length) {
        var batches = [];
        for (var i = 0; i < guestItems.length; i += MAX_BATCH) {
          batches.push(guestItems.slice(i, i + MAX_BATCH).map(function (item) {
            var copy = normalizeItem(item);
            copy.source = "merge";
            return copy;
          }));
        }
        merge = batches.reduce(function (chain, batch) {
          return chain.then(function () {
            return apiPost({ action: "merge", items: batch });
          });
        }, merge).then(function () {
          clearGuest();
        }).catch(function (error) {
          warnOnce("guest merge failed (kept locally)", error);
        });
      }
      merge.then(function () {
        return self.refresh();
      });
    },

    has(variantId) {
      if (variantId == null) return false;
      var id = String(variantId);
      // Reading this.items registers the Alpine dependency, so button
      // bindings re-evaluate on both variant switches and store changes.
      return this.items.some(function (item) {
        return item.variantId === id;
      });
    },

    toggle(item) {
      var normalized = normalizeItem(item);
      if (!normalized) return Promise.resolve(false);
      if (this.has(normalized.variantId)) {
        return this.remove(normalized.variantId);
      }
      return this.add(normalized);
    },

    add(item) {
      var normalized = normalizeItem(item);
      if (!normalized || this.has(normalized.variantId)) return Promise.resolve(false);
      var previous = this.items.slice();
      var next = previous.concat([normalized]);
      this._commit(next, !this.loggedIn);
      this._notify();
      if (!this.loggedIn) return Promise.resolve(true);
      var self = this;
      return apiPost({ action: "add", items: [normalized] }).then(function () {
        return true;
      }).catch(function (error) {
        warnOnce("add failed, rolled back", error);
        self._commit(previous, false);
        self._notify();
        return false;
      });
    },

    remove(variantId) {
      var id = String(variantId);
      var previous = this.items.slice();
      var next = previous.filter(function (item) {
        return item.variantId !== id;
      });
      if (next.length === previous.length) return Promise.resolve(false);
      this._commit(next, !this.loggedIn);
      this._notify();
      if (!this.loggedIn) return Promise.resolve(true);
      var self = this;
      return apiPost({ action: "remove", variantIds: [id] }).then(function () {
        return true;
      }).catch(function (error) {
        warnOnce("remove failed, rolled back", error);
        self._commit(previous, false);
        self._notify();
        return false;
      });
    },

    refresh() {
      var self = this;
      if (!this.loggedIn) {
        this._commit(readGuest(), false);
        this.ready = true;
        this._notify();
        return Promise.resolve(this.items);
      }
      return apiGet().then(function (result) {
        if (result.status === 401) {
          // Session considered logged out by the proxy: fall back to guest.
          self.loggedIn = false;
          self._commit(readGuest(), false);
        } else if (result.payload && result.payload.ok && Array.isArray(result.payload.items)) {
          self._commit(result.payload.items.map(normalizeItem).filter(Boolean), false);
        } else {
          warnOnce("unexpected wishlist payload");
        }
        self.ready = true;
        self._notify();
        return self.items;
      }).catch(function (error) {
        warnOnce("wishlist fetch failed", error);
        self.ready = true;
        self._notify();
        return self.items;
      });
    },

    _commit(items, persistGuest) {
      this.items = items;
      this.count = items.length;
      if (persistGuest) writeGuest(items);
    },

    _notify() {
      try {
        document.dispatchEvent(new CustomEvent("od:wishlist:changed", {
          detail: { count: this.count, ready: this.ready }
        }));
      } catch (e) {
        /* CustomEvent unavailable — nothing depends on it then */
      }
    }
  };

  function getStore() {
    try {
      if (window.Alpine && window.Alpine.store) return window.Alpine.store("wishlist");
    } catch (e) {
      /* Alpine not ready */
    }
    return null;
  }

  function registerStore() {
    try {
      // Registers over theme.liquid's {count:0} stub regardless of order:
      // the stub only seeds when no store exists, and this call replaces it.
      window.Alpine.store("wishlist", storeDef);
    } catch (error) {
      warnOnce("store registration failed", error);
    }
  }

  document.addEventListener("alpine:init", registerStore);
  if (window.Alpine && window.Alpine.store) registerStore();

  /* ------------------------------------------------------------------ */
  /* PDP button helper                                                   */
  /* ------------------------------------------------------------------ */

  function toggleFromButton(button) {
    var store = getStore();
    if (!store || typeof store.toggle !== "function") return;
    var wrapper = button.closest("[data-od-wishlist-epi]");
    if (!wrapper) return;
    var metaNode = wrapper.querySelector("[data-od-wishlist-meta]");
    var meta;
    try {
      meta = JSON.parse(metaNode.textContent);
    } catch (error) {
      warnOnce("bad wishlist meta JSON", error);
      return;
    }
    var variantId = String(button.getAttribute("data-variant-id") || "");
    var variant = (meta.variants && meta.variants[variantId]) || {};
    store.toggle({
      variantId: variantId,
      productId: String(meta.productId || ""),
      productHandle: meta.handle || "",
      productTitle: meta.title || "",
      variantTitle: variant.title || "",
      sku: variant.sku || "",
      priceCentsAtAdd: variant.priceCents,
      // image_url emits protocol-relative //cdn... URLs; store https so the
      // backend's emails (which can't resolve //) get a loadable src.
      imageUrl: meta.image ? String(meta.image).replace(/^\/\//, "https://") : "",
      // Coming-soon titles are stored as "notify" requests: own section on
      // the wishlist page, availability email when the title is released.
      itemType: meta.itemType === "notify" ? "notify" : "wishlist",
      source: "pdp"
    });
    // Small scale pop on every toggle.
    button.classList.remove("od-wishlist-pop");
    void button.offsetWidth; /* restart the animation */
    button.classList.add("od-wishlist-pop");
  }

  window.ODWishlist = window.ODWishlist || {};
  window.ODWishlist.toggleFromButton = toggleFromButton;
  window.ODWishlist.store = getStore;

  /* ------------------------------------------------------------------ */
  /* Wishlist page renderer                                              */
  /* ------------------------------------------------------------------ */

  var productCache = {}; // handle -> Promise<product.js payload | null>
  // Settled results by handle. Lets a re-render hydrate already-fetched
  // cards in the same task's microtasks (i.e. before the next paint), so
  // removals/undo never flash the per-card skeletons.
  var productCacheValue = {};

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch("/products/" + encodeURIComponent(handle) + ".js", {
        headers: { Accept: "application/json" }
      }).then(function (response) {
        if (!response.ok) return null;
        return response.json();
      }).catch(function () {
        return null;
      }).then(function (product) {
        productCacheValue[handle] = product;
        return product;
      });
    }
    return productCache[handle];
  }

  function esc(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatPrice(cents) {
    try {
      if (window.Alpine && window.Alpine.store) {
        var main = window.Alpine.store("main");
        if (main && typeof main.getPrice === "function") return main.getPrice(cents);
      }
    } catch (e) {
      /* fall through */
    }
    return "$" + (cents / 100).toFixed(2).replace(/\.00$/, "");
  }

  function productUrl(handle, variantId) {
    var url = "/products/" + encodeURIComponent(handle || "");
    if (variantId) url += "?variant=" + encodeURIComponent(variantId);
    return url;
  }

  /* SPA product transition: the same x-element-transition-trigger directive
     collection cards use (snippets/card-product.liquid), so wishlist links
     morph into the PDP with the identical animation instead of a full page
     load. The attribute VALUE is an Alpine-evaluated JS expression; JSON is
     a valid JS object literal, so a stringified payload — esc()-escaped for
     the attribute — is exactly what the directive's evaluate() expects. */
  function productTransitionAttr(imageUrl, title, isComingSoon) {
    var src = String(imageUrl || "");
    if (src.indexOf("//") === 0) src = "https:" + src;
    var payload = {
      productImg: src,
      title: String(title || ""),
      mediaCount: 1,
      isComingSoon: !!isComingSoon
    };
    return ' x-element-transition-trigger:product.animate="' + esc(JSON.stringify(payload)) + '"';
  }

  /* Alpine's mutation observer treats nodes removed and re-added within one
     mutation batch as "moved" and never binds their directives — the pitfall
     app.js documents (and fixes the same way) for the coming-soon grid. Our
     innerHTML renders followed by same-task hydration replacements, and
     especially the re-homing appendChild moves, hit exactly that: so after
     every injection/replacement/move, re-init any subtree that still has an
     unbound trigger. Safe to repeat: the directive tears down its own prior
     binding on re-init, and this file's markup carries no other Alpine
     directives (cards/rows/banner/toast are vanilla event-delegated DOM). */
  function ensureTransitionTriggers(container) {
    if (!container || !container.isConnected) return;
    if (!window.Alpine || typeof window.Alpine.initTree !== "function") return;
    var anchors = container.querySelectorAll("a");
    var unbound = false;
    for (var i = 0; i < anchors.length && !unbound; i++) {
      if (anchors[i]._odElementTransitionCleanup) continue;
      var names = anchors[i].getAttributeNames();
      for (var n = 0; n < names.length; n++) {
        if (names[n].indexOf("x-element-transition-trigger") === 0) {
          unbound = true;
          break;
        }
      }
    }
    if (!unbound) return;
    try {
      window.Alpine.initTree(container);
    } catch (error) {
      warnOnce("transition rebind failed", error);
    }
  }

  /* Title treatment (same split the PDP does in product-details-info.liquid):
     the text before the FIRST " - " (work/composer) renders 900, the
     remainder (cast/conductor/venue/date) renders 700 via the base style.
     Titles with no " - " render 900 in full. Both halves are escaped. */
  function splitTitle(title) {
    var text = String(title || "");
    var idx = text.indexOf(" - ");
    if (idx > 0) {
      return { work: text.slice(0, idx), detail: text.slice(idx + 3) };
    }
    return { work: text, detail: "" };
  }

  function titleMarkup(title) {
    var parts = splitTitle(title);
    if (parts.detail) {
      return "<strong>" + esc(parts.work) + "</strong>" + esc(" - " + parts.detail);
    }
    return "<strong>" + esc(parts.work) + "</strong>";
  }

  /* Format button label from a variant title. "Default Title" is this
     store's convention for a single-format CD product; "download" never
     appears in the UI (e.g. "MP3 download" -> "MP3"). */
  function formatLabel(variantTitle) {
    var text = String(variantTitle || "").trim();
    if (!text || text === "Default Title") return "CD";
    text = text.replace(/\bdownloads?\b/gi, "").replace(/\s{2,}/g, " ").trim();
    return text || "CD";
  }

  /* Sub-price note ("2 CDs", else just the format name): a disc count is
     shown only when the variant title states one explicitly. */
  function formatNote(variantTitle) {
    var match = /^(\d+)\s*CDs?\b/i.exec(String(variantTitle || "").trim());
    if (match && Number(match[1]) > 1) return match[1] + " CDs";
    return formatLabel(variantTitle);
  }

  function postCartAdd(variantId) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok || payload.status === 422) {
          throw new Error(payload.description || "cart add failed");
        }
        return payload;
      });
    });
  }

  // Same follow-up the theme uses (assets/app.js Sunrise.updateQuantity):
  // resync the Alpine cart store, then announce 'cart-updated' so the
  // drawer and badge refresh.
  function resyncCart() {
    var cartStore = window.Alpine && window.Alpine.store && window.Alpine.store("cart");
    var resync = Promise.resolve();
    if (cartStore && typeof cartStore.refreshCart === "function") {
      resync = cartStore.refreshCart().catch(function () {});
    } else if (cartStore && typeof cartStore.applyCartState === "function") {
      resync = fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
        cartStore.applyCartState(cart);
      }).catch(function () {});
    }
    return Promise.resolve(resync).then(function () {
      window.dispatchEvent(new Event("cart-updated"));
    });
  }

  function skeletonMarkup() {
    var card = '<div class="od-wl-card od-wl-card--skeleton">' +
      '<div class="od-wl-skeleton od-wl-skeleton--image"></div>' +
      '<div class="od-wl-skeleton od-wl-skeleton--line"></div>' +
      '<div class="od-wl-skeleton od-wl-skeleton--line od-wl-skeleton--short"></div>' +
      "</div>";
    return '<div class="od-wl-container" aria-hidden="true">' +
      '<div class="od-wl-grid">' + card + card + card + card + "</div>" +
      "</div>";
  }

  function emptyMarkup(browseUrl) {
    return '<div class="od-wl-container"><div class="od-wl-empty">' +
      "<p>Your wishlist is empty.</p>" +
      '<p class="od-wl-empty__hint">Tap the heart on any recording to save it for later.</p>' +
      '<a class="od-wl-btn od-wl-btn--primary" href="' + esc(browseUrl) + '" x-element-transition-trigger>Browse recordings</a>' +
      "</div></div>";
  }

  function loginHref(loginUrl) {
    return loginUrl + (loginUrl.indexOf("?") === -1 ? "?" : "&") + "return_url=%2Fpages%2Fwishlist";
  }

  function guestBannerMarkup(loginUrl) {
    return '<div class="od-wl-container"><div class="od-wl-guest-note">' +
      '<a href="' + esc(loginHref(loginUrl)) + '">Log in</a>' +
      " to save your wishlist across devices and get alerts." +
      "</div></div>";
  }

  /* --- Collection-scoped sale awareness ------------------------------
     od-sale-display.js only decorates a price element for a collection-
     scoped sale when the element declares the product's collection handles
     (data-od-sale-collections). Product JSON (/products/x.js) doesn't
     expose collections, so the lookup is inverted: fetch the product ids
     of just the collections referenced by live sales (usually none or a
     handful) and stamp matching handles onto each card's price element
     before insertion. Fresh per page view (5-min session cache), works
     for guests, and picks up sale collections created after items were
     wishlisted. Storewide sales need none of this — od-sale-display
     matches them without the attribute. */
  var SALE_ENDPOINT = "/apps/library/active-sale";
  var SALE_DISPLAY_CACHE_KEY = "od-active-sale-v2"; // od-sale-display.js's cache, reused read-only
  var SALE_MAP_CACHE_KEY = "od-wl-sale-collections-v1";
  var SALE_CACHE_TTL_MS = 5 * 60 * 1000;
  var COLLECTION_PAGE_LIMIT = 250;
  var COLLECTION_MAX_PAGES = 8;
  var saleHandleMapPromise = null;

  function readSessionCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.at > SALE_CACHE_TTL_MS) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeSessionCache(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value: value }));
    } catch (error) {
      /* private mode etc. — recompute next view */
    }
  }

  function saleIsLive(sale) {
    if (!sale) return false;
    if (sale.endsAt && Date.parse(sale.endsAt) <= Date.now()) return false;
    var percentage = Number(sale.percentage);
    return isFinite(percentage) && percentage > 0 && percentage <= 100;
  }

  function fetchLiveSales() {
    var cached = readSessionCache(SALE_DISPLAY_CACHE_KEY);
    if (cached && Array.isArray(cached.sales)) {
      return Promise.resolve(cached.sales.filter(saleIsLive));
    }
    return fetch(SALE_ENDPOINT, { headers: { Accept: "application/json" } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (payload) {
        if (!payload || !payload.ok) return [];
        var list = Array.isArray(payload.sales)
          ? payload.sales
          : payload.sale
            ? [payload.sale]
            : [];
        return list.filter(saleIsLive);
      })
      .catch(function () { return []; });
  }

  function fetchCollectionProductIds(handle) {
    var ids = [];
    function fetchPage(page) {
      return fetch(
        "/collections/" + encodeURIComponent(handle) + "/products.json?limit=" +
          COLLECTION_PAGE_LIMIT + "&page=" + page,
        { headers: { Accept: "application/json" } }
      )
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (payload) {
          var products = payload && Array.isArray(payload.products) ? payload.products : [];
          for (var i = 0; i < products.length; i++) ids.push(String(products[i].id));
          if (products.length === COLLECTION_PAGE_LIMIT && page < COLLECTION_MAX_PAGES) {
            return fetchPage(page + 1);
          }
          return ids;
        })
        .catch(function () { return ids; });
    }
    return fetchPage(1);
  }

  // Resolves to { "<productId>": "handle1,handle2", ... } covering only
  // collections referenced by live sales. {} when no collection sale runs.
  function loadSaleCollectionHandleMap() {
    if (saleHandleMapPromise) return saleHandleMapPromise;
    var cached = readSessionCache(SALE_MAP_CACHE_KEY);
    if (cached && cached.value && typeof cached.value === "object") {
      saleHandleMapPromise = Promise.resolve(cached.value);
      return saleHandleMapPromise;
    }
    saleHandleMapPromise = fetchLiveSales()
      .then(function (sales) {
        var handles = [];
        for (var i = 0; i < sales.length; i++) {
          var cols = sales[i].collections || [];
          for (var j = 0; j < cols.length; j++) {
            if (cols[j] && cols[j].handle && handles.indexOf(cols[j].handle) === -1) {
              handles.push(cols[j].handle);
            }
          }
        }
        if (!handles.length) return {};
        return Promise.all(handles.map(fetchCollectionProductIds)).then(function (idLists) {
          var map = {};
          for (var h = 0; h < handles.length; h++) {
            for (var k = 0; k < idLists[h].length; k++) {
              var id = idLists[h][k];
              map[id] = map[id] ? map[id] + "," + handles[h] : handles[h];
            }
          }
          return map;
        });
      })
      .then(function (map) {
        writeSessionCache(SALE_MAP_CACHE_KEY, map);
        return map;
      });
    return saleHandleMapPromise;
  }

  /* ---- grouping ------------------------------------------------------ */

  // One card per PRODUCT: group the stored rows by product handle, newest
  // first inside each group (the newest row picks the preselected format).
  function groupItems(items) {
    var byKey = {};
    var groups = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var key = item.productHandle || "id:" + (item.productId || item.variantId);
      if (!byKey[key]) {
        byKey[key] = { handle: item.productHandle, items: [] };
        groups.push(byKey[key]);
      }
      byKey[key].items.push(item);
    }
    for (var g = 0; g < groups.length; g++) {
      groups[g].items.sort(function (a, b) {
        return (Date.parse(b.addedAt) || 0) - (Date.parse(a.addedAt) || 0);
      });
      groups[g].latest = groups[g].items[0];
    }
    return groups;
  }

  // Initial (pre-hydration) classification: a group whose stored rows are
  // ALL notify requests starts in Section B; anything else starts as a
  // Section A card. Hydration re-homes on disagreement with live data —
  // exactly the classification the blocking renderer used to compute
  // up front.
  function isNotifyGroup(group) {
    for (var i = 0; i < group.items.length; i++) {
      if (group.items[i].itemType !== "notify") return false;
    }
    return true;
  }

  function sellableVariants(product) {
    var list = [];
    if (product && Array.isArray(product.variants)) {
      for (var i = 0; i < product.variants.length; i++) {
        var variant = product.variants[i];
        // FLAC UPGRADE variants are owner-only add-ons, never a format
        // choice (same exclusion as snippets/_price-element.liquid).
        if (variant.option1 === "FLAC UPGRADE") continue;
        if (String(variant.sku || "").toUpperCase().indexOf("FLACUP") === 0) continue;
        list.push(variant);
      }
    }
    return list;
  }

  // Preselect the most recently added STORED variant (available first),
  // then any available variant, then any stored match, then the first.
  function pickSelected(variants, group) {
    var stored = group.items; // newest first
    var i, j;
    for (i = 0; i < stored.length; i++) {
      for (j = 0; j < variants.length; j++) {
        if (String(variants[j].id) === stored[i].variantId && variants[j].available) return variants[j];
      }
    }
    for (j = 0; j < variants.length; j++) {
      if (variants[j].available) return variants[j];
    }
    for (i = 0; i < stored.length; i++) {
      for (j = 0; j < variants.length; j++) {
        if (String(variants[j].id) === stored[i].variantId) return variants[j];
      }
    }
    return variants[0] || null;
  }

  function storedIdsOf(group) {
    return group.items.map(function (item) { return item.variantId; }).join(",");
  }

  /* ---- card / row markup --------------------------------------------- */

  // The theme's standard badge pill (snippets/_discount-badge.liquid +
  // card render class 'py-1 bottom left'). Two pills share the media slot:
  // the compare-at badge (filled by syncCardToSelection) and the
  // od-sale-display.js placeholder (data-od-sale-pct-badge — that script
  // fills, reveals and, when a sale wins, hides the compare-at sibling).
  var BADGE_CLASS = "card-product__badge-container z-10 rounded-[40px] bg-bg-500 px-3 text-sm py-1 bottom left";

  function badgesMarkup(saleHandles) {
    return '<div class="' + BADGE_CLASS + '" data-od-wl-compare-badge hidden>' +
      '<span class="font-semibold"></span></div>' +
      '<div class="' + BADGE_CLASS + '" data-od-sale-pct-badge' +
      (saleHandles ? ' data-od-sale-collections="' + esc(saleHandles) + '"' : "") +
      ' hidden><span class="font-semibold"></span></div>';
  }

  function imageMarkup(src, alt) {
    if (src) {
      var url = String(src);
      if (url.indexOf("//") === 0) url = "https:" + url;
      return '<img class="od-wl-card__img" src="' + esc(url) + '" alt="' + esc(alt) + '" loading="lazy">';
    }
    return '<div class="od-wl-card__img od-wl-card__img--missing" aria-hidden="true">♪</div>';
  }

  // _price-element-shaped wrapper: od-sale-display.js's MutationObserver
  // finds [data-od-sale-base] nodes added later and decorates the
  // .text-primary-600 span with the live sale price. KEEP this shape.
  function priceElementMarkup(priceCents, compareCents, note, saleHandles) {
    var trailing = "";
    if (isFinite(compareCents) && compareCents > priceCents) {
      trailing = '<s class="od-wl-price__compare">' + esc(formatPrice(compareCents)) + "</s>";
    } else if (note) {
      trailing = '<span class="od-wl-price__note">' + esc(note) + "</span>";
    }
    return '<div class="price__element flex items-center gap-2 text-text-500 od-wl-price" data-od-sale-base="' + esc(priceCents) + '"' +
      (saleHandles ? ' data-od-sale-collections="' + esc(saleHandles) + '"' : "") + ">" +
      '<span class="text-primary-600 od-wl-price__current">' + esc(formatPrice(priceCents)) + "</span>" +
      trailing +
      "</div>";
  }

  function availableCardMarkup(group, product, saleHandles, isRelease) {
    var variants = sellableVariants(product);
    var selected = pickSelected(variants, group);
    var selectedId = selected ? String(selected.id) : group.latest.variantId;
    var url = productUrl(product.handle, selectedId);
    var image = product.featured_image || group.latest.imageUrl;
    var transition = productTransitionAttr(image, product.title, false);
    var buttons = "";
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var isSelected = String(variant.id) === selectedId;
      buttons += '<button type="button" role="radio" class="od-wl-format' +
        (isSelected ? " od-wl-format--selected" : "") +
        (variant.available ? "" : " od-wl-format--soldout") + '"' +
        ' aria-checked="' + (isSelected ? "true" : "false") + '"' +
        ' tabindex="' + (isSelected ? "0" : "-1") + '"' +
        " data-od-wl-format" +
        ' data-variant-id="' + esc(variant.id) + '"' +
        ' data-price="' + esc(variant.price) + '"' +
        ' data-compare="' + esc(variant.compare_at_price || 0) + '"' +
        ' data-available="' + (variant.available ? "1" : "") + '"' +
        ' data-note="' + esc(formatNote(variant.title)) + '"' +
        (variant.available ? "" : ' title="Currently unavailable"') +
        ">" + esc(formatLabel(variant.title)) + "</button>";
    }
    return '<article class="od-wl-card" data-od-wl-card' +
      ' data-handle="' + esc(product.handle) + '"' +
      ' data-product-title="' + esc(product.title) + '"' +
      ' data-selected-variant="' + esc(selectedId) + '"' +
      ' data-stored-ids="' + esc(storedIdsOf(group)) + '"' +
      (saleHandles ? ' data-sale-handles="' + esc(saleHandles) + '"' : "") +
      ">" +
      '<a class="od-wl-card__media" href="' + esc(url) + '" data-od-wl-pdp' + transition + ' tabindex="-1" aria-hidden="true">' +
      imageMarkup(image, product.title) +
      badgesMarkup(saleHandles) +
      // Placed AFTER both discount pills so the CSS sibling rule can hide
      // this flag whenever either discount badge is visible (discount wins).
      (isRelease ? '<div class="od-wl-release-flag">Just Released</div>' : "") +
      "</a>" +
      '<h3 class="od-wl-card__title" title="' + esc(product.title) + '">' +
      '<a href="' + esc(url) + '" data-od-wl-pdp' + transition + ">" + titleMarkup(product.title) + "</a></h3>" +
      '<div class="od-wl-card__formats" role="radiogroup" aria-label="Format">' + buttons + "</div>" +
      '<div class="od-wl-card__price-slot" data-od-wl-price-slot></div>' +
      '<button type="button" class="od-wl-cart-btn" data-od-wl-cart>Add to Cart</button>' +
      '<p class="od-wl-card__error" data-od-wl-error hidden></p>' +
      '<button type="button" class="od-wl-remove-btn" data-od-wl-remove>Remove</button>' +
      "</article>";
  }

  // Product fetch failed: render from the stored row alone — stored title,
  // image and price-at-add, no format selector (the page never hard-breaks
  // on an API failure). Add to Cart still posts the stored variant; Shopify
  // rejects it with a clear inline error if it is no longer sellable.
  function fallbackCardMarkup(group, saleHandles) {
    var item = group.latest;
    var url = productUrl(item.productHandle, item.variantId);
    var transition = productTransitionAttr(item.imageUrl, item.productTitle, false);
    return '<article class="od-wl-card" data-od-wl-card data-od-wl-fallback' +
      ' data-handle="' + esc(item.productHandle) + '"' +
      ' data-product-title="' + esc(item.productTitle) + '"' +
      ' data-selected-variant="' + esc(item.variantId) + '"' +
      ' data-stored-ids="' + esc(storedIdsOf(group)) + '">' +
      '<a class="od-wl-card__media" href="' + esc(url) + '" data-od-wl-pdp' + transition + ' tabindex="-1" aria-hidden="true">' +
      imageMarkup(item.imageUrl, item.productTitle) +
      "</a>" +
      '<h3 class="od-wl-card__title" title="' + esc(item.productTitle) + '">' +
      '<a href="' + esc(url) + '" data-od-wl-pdp' + transition + ">" + titleMarkup(item.productTitle) + "</a></h3>" +
      '<div class="od-wl-card__price-slot" data-od-wl-price-slot>' +
      priceElementMarkup(item.priceCentsAtAdd, 0, formatNote(item.variantTitle), saleHandles) +
      "</div>" +
      '<button type="button" class="od-wl-cart-btn" data-od-wl-cart>Add to Cart</button>' +
      '<p class="od-wl-card__error" data-od-wl-error hidden></p>' +
      '<button type="button" class="od-wl-remove-btn" data-od-wl-remove>Remove</button>' +
      "</article>";
  }

  // Pre-hydration Section A card: stored title/cover paint immediately, the
  // format-selector and price areas hold small skeletons, and Add to Cart
  // stays disabled until /products/{handle}.js hydrates the card in place.
  // Remove (data-stored-ids) works before hydration.
  function pendingCardMarkup(group, groupIndex) {
    var item = group.latest;
    var url = productUrl(item.productHandle, item.variantId);
    var transition = productTransitionAttr(item.imageUrl, item.productTitle, false);
    return '<article class="od-wl-card" data-od-wl-card data-od-wl-pending' +
      ' data-od-wl-group="' + groupIndex + '"' +
      ' data-handle="' + esc(item.productHandle) + '"' +
      ' data-product-title="' + esc(item.productTitle) + '"' +
      ' data-selected-variant="' + esc(item.variantId) + '"' +
      ' data-stored-ids="' + esc(storedIdsOf(group)) + '">' +
      '<a class="od-wl-card__media" href="' + esc(url) + '" data-od-wl-pdp' + transition + ' tabindex="-1" aria-hidden="true">' +
      imageMarkup(item.imageUrl, item.productTitle) +
      "</a>" +
      '<h3 class="od-wl-card__title" title="' + esc(item.productTitle) + '">' +
      '<a href="' + esc(url) + '" data-od-wl-pdp' + transition + ">" + titleMarkup(item.productTitle) + "</a></h3>" +
      '<div class="od-wl-card__formats" aria-hidden="true"><span class="od-wl-skeleton od-wl-skeleton--format"></span></div>' +
      '<div class="od-wl-card__price-slot"><span class="od-wl-skeleton od-wl-skeleton--price"></span></div>' +
      '<button type="button" class="od-wl-cart-btn" data-od-wl-cart disabled>Add to Cart</button>' +
      '<p class="od-wl-card__error" data-od-wl-error hidden></p>' +
      '<button type="button" class="od-wl-remove-btn" data-od-wl-remove>Remove</button>' +
      "</article>";
  }

  // pendingIndex (number) marks the row as awaiting hydration: the row is
  // fully usable from stored data ("Coming Soon" copy), and hydration only
  // refreshes it from the live record — or re-homes it to Section A when
  // the title turns out to be released. Pass null for a hydrated row.
  function notifyRowMarkup(group, product, pendingIndex) {
    var item = group.latest;
    var title = (product && product.title) || item.productTitle;
    var handle = (product && product.handle) || item.productHandle;
    var image = (product && product.featured_image) || item.imageUrl;
    var url = productUrl(handle, null);
    var transition = productTransitionAttr(image, title, true);
    return '<article class="od-wl-row" data-od-wl-card' +
      (pendingIndex != null
        ? " data-od-wl-pending" + ' data-od-wl-group="' + pendingIndex + '"'
        : "") +
      ' data-handle="' + esc(handle) + '"' +
      ' data-product-title="' + esc(title) + '"' +
      ' data-stored-ids="' + esc(storedIdsOf(group)) + '">' +
      '<a class="od-wl-row__thumb" href="' + esc(url) + '"' + transition + ' tabindex="-1" aria-hidden="true">' +
      imageMarkup(image, title) +
      "</a>" +
      '<div class="od-wl-row__mid">' +
      '<span class="od-wl-chip">Coming Soon</span>' +
      '<h3 class="od-wl-card__title od-wl-row__title" title="' + esc(title) + '">' +
      '<a href="' + esc(url) + '"' + transition + ">" + titleMarkup(title) + "</a></h3>" +
      "</div>" +
      '<div class="od-wl-row__actions">' +
      '<span class="od-wl-notify-flag">Notification On</span>' +
      '<button type="button" class="od-wl-row-remove" data-od-wl-remove>Remove</button>' +
      "</div>" +
      "</article>";
  }

  /* ---- release announcement banner ------------------------------------- */

  /* A wishlist "release" is a notify request whose availability email went
     out recently (notifiedAt, server-stamped) for a product that is LIVE-
     available THIS render — i.e. it classified into Section A in the same
     pass. Qualification runs inside renderPage's classification loop on the
     same groups/products arrays that build the section markup, so the banner
     and the lists can never disagree (nothing is refetched). Guests never
     receive notifiedAt (no emails), so the banner never renders for them. */

  var RELEASE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
  var RELEASE_DISMISS_TTL_MS = 60 * 24 * 60 * 60 * 1000;
  var RELEASE_DISMISS_PREFIX = "od-wl-release-dismissed:v1:";
  var RELEASE_COVER_CAP = 4;

  // Dismissals are per customer AND per release: one localStorage key per
  // customer holding { "<variantId>": dismissedAtIso }, pruned on read.
  function releaseDismissKey() {
    var customerId = "guest";
    try {
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        customerId = String(window.Shopify.customer.id);
      }
    } catch (e) {
      /* fall through to guest */
    }
    return RELEASE_DISMISS_PREFIX + customerId;
  }

  function readDismissedReleases() {
    try {
      var raw = localStorage.getItem(releaseDismissKey());
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return {};
      var pruned = {};
      var changed = false;
      for (var key in parsed) {
        if (!Object.prototype.hasOwnProperty.call(parsed, key)) continue;
        var at = Date.parse(parsed[key]);
        if (isFinite(at) && Date.now() - at < RELEASE_DISMISS_TTL_MS) {
          pruned[key] = parsed[key];
        } else {
          changed = true;
        }
      }
      if (changed) localStorage.setItem(releaseDismissKey(), JSON.stringify(pruned));
      return pruned;
    } catch (e) {
      return {};
    }
  }

  function dismissReleaseVariants(variantIds) {
    try {
      var map = readDismissedReleases();
      var now = new Date().toISOString();
      for (var i = 0; i < variantIds.length; i++) map[variantIds[i]] = now;
      localStorage.setItem(releaseDismissKey(), JSON.stringify(map));
    } catch (e) {
      /* private mode etc. — the banner simply returns next page view */
    }
  }

  // Called only for groups that classified into Section A with a live
  // product record. Returns null (no banner entry, no card flag) or the
  // group's release descriptor. Dismissing hides the banner AND the card
  // flag but never touches the wishlist rows themselves.
  function releaseFromGroup(group, product, dismissed) {
    var qualifying = [];
    for (var i = 0; i < group.items.length; i++) {
      var item = group.items[i];
      if (item.itemType !== "notify" || !item.notifiedAt) continue;
      var at = Date.parse(item.notifiedAt);
      if (!isFinite(at) || Date.now() - at > RELEASE_WINDOW_MS) continue;
      if (dismissed[item.variantId]) continue;
      qualifying.push(item);
    }
    if (!qualifying.length) return null;
    qualifying.sort(function (a, b) {
      return (Date.parse(b.notifiedAt) || 0) - (Date.parse(a.notifiedAt) || 0);
    });
    var lead = qualifying[0]; // most recently notified variant fronts the link
    return {
      variantIds: qualifying.map(function (item) { return item.variantId; }),
      url: productUrl(product.handle, lead.variantId),
      title: product.title,
      image: product.featured_image || lead.imageUrl || ""
    };
  }

  // Cheap stored-data-only predicate: does this group hold a fresh,
  // undismissed notify request? Used to pick which groups hydrate with
  // priority (the banner must not wait for viewport-driven hydration).
  // Live availability is still confirmed by releaseFromGroup afterwards.
  function groupMayBeRelease(group, dismissed) {
    for (var i = 0; i < group.items.length; i++) {
      var item = group.items[i];
      if (item.itemType !== "notify" || !item.notifiedAt) continue;
      var at = Date.parse(item.notifiedAt);
      if (!isFinite(at) || Date.now() - at > RELEASE_WINDOW_MS) continue;
      if (dismissed[item.variantId]) continue;
      return true;
    }
    return false;
  }

  function releaseCoverMarkup(release) {
    var src = String(release.image || "");
    if (src.indexOf("//") === 0) src = "https:" + src;
    var img = src
      ? '<img class="od-wl-release__img" src="' + esc(src) + '" alt="' + esc(release.title) + '" loading="lazy">'
      : '<span class="od-wl-release__img od-wl-release__img--missing" aria-hidden="true">♪</span>';
    return '<a class="od-wl-release__cover" href="' + esc(release.url) + '"' +
      productTransitionAttr(release.image, release.title, false) + ">" + img + "</a>";
  }

  // Renders NOTHING (empty string — no container, no spacing) when no
  // release qualifies. 2+ releases share ONE banner, never a stack.
  function releaseBannerMarkup(releases) {
    if (!releases || !releases.length) return "";
    var allIds = [];
    for (var i = 0; i < releases.length; i++) {
      for (var j = 0; j < releases[i].variantIds.length; j++) {
        allIds.push(releases[i].variantIds[j]);
      }
    }
    var media, headline, sub = "", action;
    if (releases.length === 1) {
      var parts = splitTitle(releases[0].title); // same first-" - " split as the cards
      media = releaseCoverMarkup(releases[0]);
      headline = esc(parts.work) + " is now available!";
      if (parts.detail) {
        sub = '<p class="od-wl-release__sub">' + esc(parts.detail) + "</p>";
      }
      action = '<a class="od-wl-release-btn" href="' + esc(releases[0].url) + '"' +
        productTransitionAttr(releases[0].image, releases[0].title, false) +
        ">Shop the Release</a>";
    } else {
      var covers = "";
      for (var c = 0; c < releases.length && c < RELEASE_COVER_CAP; c++) {
        covers += releaseCoverMarkup(releases[c]);
      }
      if (releases.length > RELEASE_COVER_CAP) {
        covers += '<span class="od-wl-release__cover od-wl-release__more">+' +
          (releases.length - RELEASE_COVER_CAP) + "</span>";
      }
      media = '<div class="od-wl-release__covers">' + covers + "</div>";
      headline = releases.length + " recordings from your wishlist are now available!";
      // Consistency decision: the button label stays "Shop the …" and the
      // multi variant scrolls to Section A's grid (in the DOM this render).
      action = '<button type="button" class="od-wl-release-btn" data-od-wl-release-shop>Shop the Releases</button>';
    }
    return '<div class="od-wl-container od-wl-release-wrap">' +
      '<section class="od-wl-release" data-od-wl-release' +
      ' data-od-wl-release-ids="' + esc(allIds.join(",")) + '"' +
      ' aria-label="New release announcement">' +
      '<div class="od-wl-release__glow" aria-hidden="true"></div>' +
      media +
      '<div class="od-wl-release__text">' +
      '<div class="od-wl-release__chips">' +
      '<span class="od-wl-release-chip">Just Released</span>' +
      '<span class="od-wl-release__note">You asked to be notified &mdash; it&rsquo;s here.</span>' +
      "</div>" +
      '<h2 class="od-wl-release__headline">' + headline + "</h2>" +
      sub +
      "</div>" +
      '<div class="od-wl-release__actions">' +
      action +
      '<button type="button" class="od-wl-release-dismiss" data-od-wl-release-dismiss>Dismiss</button>' +
      "</div>" +
      "</section></div>";
  }

  /* ---- section shells -------------------------------------------------- */

  function pillText(count) {
    return count + (count === 1 ? " item" : " items");
  }

  // Both section shells ALWAYS render (hidden when empty) so hydration can
  // re-home a card/row between sections with a surgical DOM move instead of
  // a full re-render; updateSectionCounts flips visibility and pill text.
  function sectionAMarkup(cardsHtml, count, browseUrl) {
    return '<section class="od-wl-avail"><div class="od-wl-container">' +
      '<div class="od-wl-sec-head">' +
      '<h2 class="od-wl-sec-head__title">Available for Purchase</h2>' +
      '<span class="od-wl-pill od-wl-pill--purple" data-od-wl-avail-pill>' + pillText(count) + "</span>" +
      '<button type="button" class="od-wl-add-all" data-od-wl-add-all' + (count ? "" : " hidden") + ">Add all to cart</button>" +
      "</div>" +
      '<p class="od-wl-add-all-status" data-od-wl-add-all-status hidden aria-live="polite"></p>' +
      '<div class="od-wl-grid" data-od-wl-grid' + (count ? "" : " hidden") + ">" + cardsHtml + "</div>" +
      '<div class="od-wl-a-empty" data-od-wl-a-empty' + (count ? " hidden" : "") + ">" +
      "<p>You haven&rsquo;t saved any available recordings yet.</p>" +
      '<a href="' + esc(browseUrl) + '" x-element-transition-trigger>Browse the catalog</a>' +
      "</div>" +
      "</div></section>";
  }

  function sectionBMarkup(rowsHtml, count, loggedIn, loginUrl) {
    var guestNote = loggedIn
      ? ""
      : ' <a class="od-wl-band__login" href="' + esc(loginHref(loginUrl)) + '">Log in</a> so we can email you when these become available.';
    return '<section class="od-wl-band" data-od-wl-band' + (count ? "" : " hidden") + '><div class="od-wl-container">' +
      '<div class="od-wl-sec-head od-wl-sec-head--band">' +
      '<h2 class="od-wl-sec-head__title od-wl-sec-head__title--band">Awaiting Release</h2>' +
      '<span class="od-wl-pill od-wl-pill--gold" data-od-wl-band-pill>' + pillText(count) + "</span>" +
      "</div>" +
      '<p class="od-wl-band__sub">We will notify you when these items become available for purchase.' + guestNote + "</p>" +
      '<div class="od-wl-rows" data-od-wl-rows>' + rowsHtml + "</div>" +
      "</div></section>";
  }

  function footerMarkup(browseUrl) {
    return '<footer class="od-wl-foot"><div class="od-wl-container">' +
      '<a class="od-wl-foot__link" href="' + esc(browseUrl) + '" x-element-transition-trigger>Continue browsing the catalog &rarr;</a>' +
      "</div></footer>";
  }

  /* ---- selection / interactions ---------------------------------------- */

  // Applies the selected format to the whole card: radio state, price line
  // (rebuilt so od-sale-display re-decorates the fresh node), PDP links,
  // the theme-standard compare-at badge, and the Add to Cart state.
  function syncCardToSelection(card, button) {
    var buttons = card.querySelectorAll("[data-od-wl-format]");
    for (var i = 0; i < buttons.length; i++) {
      var isSelected = buttons[i] === button;
      buttons[i].classList.toggle("od-wl-format--selected", isSelected);
      buttons[i].setAttribute("aria-checked", isSelected ? "true" : "false");
      buttons[i].setAttribute("tabindex", isSelected ? "0" : "-1");
    }
    var variantId = button.getAttribute("data-variant-id");
    var price = Number(button.getAttribute("data-price"));
    var compare = Number(button.getAttribute("data-compare"));
    var note = button.getAttribute("data-note") || "";
    var available = button.getAttribute("data-available") === "1";
    var saleHandles = card.getAttribute("data-sale-handles") || "";
    var slot = card.querySelector("[data-od-wl-price-slot]");
    if (slot) slot.innerHTML = priceElementMarkup(price, compare, note, saleHandles);
    var url = productUrl(card.getAttribute("data-handle"), variantId);
    var links = card.querySelectorAll("[data-od-wl-pdp]");
    for (var j = 0; j < links.length; j++) links[j].setAttribute("href", url);
    var badge = card.querySelector("[data-od-wl-compare-badge]");
    if (badge) {
      var label = badge.querySelector("span");
      if (label && isFinite(compare) && compare > price) {
        label.textContent = "-" + Math.round(((compare - price) * 100) / compare) + "% OFF";
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }
    var cartButton = card.querySelector("[data-od-wl-cart]");
    if (cartButton && !cartButton.hasAttribute("data-busy")) cartButton.disabled = !available;
    var error = card.querySelector("[data-od-wl-error]");
    if (error) error.hidden = true;
  }

  function selectFormat(button) {
    var card = button.closest("[data-od-wl-card]");
    if (!card) return;
    card.setAttribute("data-selected-variant", button.getAttribute("data-variant-id"));
    syncCardToSelection(card, button);
  }

  function handleCartClick(card, button) {
    var variantId = card.getAttribute("data-selected-variant");
    if (!variantId || button.disabled) return;
    var error = card.querySelector("[data-od-wl-error]");
    if (error) error.hidden = true;
    var idle = button.textContent;
    button.disabled = true;
    button.setAttribute("data-busy", "1");
    button.textContent = "Adding…";
    postCartAdd(variantId).then(function () {
      return resyncCart();
    }).then(function () {
      button.textContent = "Added ✓";
      setTimeout(function () {
        button.textContent = idle;
        button.removeAttribute("data-busy");
        button.disabled = false;
      }, 1600);
    }).catch(function (err) {
      warnOnce("cart add failed", err);
      button.textContent = idle;
      button.removeAttribute("data-busy");
      button.disabled = false;
      if (error) {
        error.textContent = "Couldn’t add to cart. Please try again.";
        error.hidden = false;
      }
    });
  }

  // Adds every in-stock card with its currently selected format, then
  // reports any failures by product title. Section A cards still awaiting
  // hydration are hydrated FIRST (same cache/pool code path, so nothing is
  // fetched twice), which also re-homes any that turn out unavailable —
  // the queue below then sees exactly the final in-stock Section A set.
  // Sequential posts, one cart resync at the end.
  function handleAddAll(root, button) {
    if (button.disabled) return;
    var status = root.querySelector("[data-od-wl-add-all-status]");
    var idle = button.textContent;
    button.disabled = true;
    button.textContent = "Adding…";
    var gen = root._odWlGen;
    var ctx = root._odWlCtx;
    var jobs = [];
    if (ctx) {
      var pendingEls = root.querySelectorAll(".od-wl-avail [data-od-wl-pending]");
      for (var p = 0; p < pendingEls.length; p++) {
        pendingEls[p]._odWlQueued = true;
        jobs.push(hydrateElement(ctx, pendingEls[p]).catch(function () {}));
      }
    }
    Promise.all(jobs).then(function () {
      if (root._odWlGen !== gen) {
        // The page re-rendered mid-hydration (removal, login change): bail
        // rather than add from a stale card set.
        button.textContent = idle;
        button.disabled = false;
        return;
      }
      var cards = root.querySelectorAll(".od-wl-avail [data-od-wl-card]");
      var queue = [];
      for (var i = 0; i < cards.length; i++) {
        var cartButton = cards[i].querySelector("[data-od-wl-cart]");
        if (!cartButton || cartButton.disabled) continue; // out-of-stock selection
        queue.push({
          variantId: cards[i].getAttribute("data-selected-variant"),
          title: cards[i].getAttribute("data-product-title") || "Item"
        });
      }
      if (!queue.length) {
        button.textContent = idle;
        button.disabled = false;
        if (status) {
          status.textContent = "Nothing in stock to add.";
          status.hidden = false;
        }
        return;
      }
      var failed = [];
      var chain = Promise.resolve();
      queue.forEach(function (entry) {
        chain = chain.then(function () {
          return postCartAdd(entry.variantId).catch(function () {
            failed.push(entry.title);
          });
        });
      });
      chain.then(function () {
        return resyncCart();
      }).then(function () {
        button.textContent = idle;
        button.disabled = false;
        if (status) {
          var added = queue.length - failed.length;
          status.textContent = failed.length
            ? "Added " + added + " of " + queue.length + " to your cart. Couldn’t add: " + failed.join(", ") + "."
            : "Added " + added + (added === 1 ? " item" : " items") + " to your cart.";
          status.classList.toggle("od-wl-add-all-status--error", failed.length > 0);
          status.hidden = false;
        }
      });
    });
  }

  var REMOVE_ANIM_MS = 180;

  // Optimistic remove of ALL stored variants of the card's product (for a
  // notify row this also cancels the notification — store.remove does
  // both), with an Undo offer in the shared toast.
  function removeCardGroup(card) {
    var store = getStore();
    if (!store) return;
    var ids = (card.getAttribute("data-stored-ids") || "").split(",").filter(Boolean);
    if (!ids.length) return;
    // Snapshot the stored rows before they disappear so Undo can re-add
    // the exact same items.
    var removed = [];
    var storeItems = store.items || [];
    for (var i = 0; i < storeItems.length; i++) {
      if (ids.indexOf(storeItems[i].variantId) !== -1) {
        removed.push(normalizeItem(storeItems[i]));
      }
    }
    card.classList.add("od-wl-leaving");
    setTimeout(function () {
      for (var j = 0; j < ids.length; j++) store.remove(ids[j]);
      if (removed.length) showUndoToast(removed);
    }, REMOVE_ANIM_MS);
  }

  function showUndoToast(items) {
    var el = ensureToastEl();
    el.innerHTML = closeButtonMarkup() +
      '<p class="od-wl-toast__msg">Removed from wishlist &mdash; ' +
      '<button type="button" class="od-wl-toast__undo" data-od-wl-toast-undo>Undo</button></p>';
    el.querySelector("[data-od-wl-toast-undo]").addEventListener("click", function () {
      var store = getStore();
      if (store) {
        for (var i = 0; i < items.length; i++) store.add(items[i]);
      }
      hideToast();
    });
    el.classList.add("od-wl-toast--visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  /* ---- progressive hydration ------------------------------------------- */

  /* First paint is instant at any wishlist size: the page renders whole
     sections from STORED data (classified by itemType), and each card/row
     carries data-od-wl-pending + data-od-wl-group. Live product records
     (/products/{handle}.js) hydrate elements in place:

       - viewport-driven: an IntersectionObserver (rootMargin 500px) feeds a
         queue drained by a pool of HYDRATE_CONCURRENCY workers;
       - priority: release-banner candidates (fresh notifiedAt) hydrate
         immediately, bypassing the observer, so the banner appears as fast
         as it did when everything blocked on Promise.all;
       - warm start: handles already settled in productCacheValue hydrate in
         the render task's microtasks, before the browser paints — re-renders
         (remove, undo, dismiss) never flash skeletons.

     Hydration re-homes an element whose live availability disagrees with
     its stored section (surgical appendChild + pill/visibility updates).
     All per-render state lives in a ctx object on the root; a new render
     bumps root._odWlGen and tears the old observer/queue down, and stale
     in-flight hydrations no-op on the gen check. */

  var HYDRATE_CONCURRENCY = 8;

  function teardownHydration(root) {
    if (root._odWlIO) {
      root._odWlIO.disconnect();
      root._odWlIO = null;
    }
    if (root._odWlCtx) {
      root._odWlCtx.queue.length = 0;
      root._odWlCtx = null;
    }
  }

  function setupHydration(ctx) {
    var pending = ctx.content.querySelectorAll("[data-od-wl-pending]");
    if (typeof IntersectionObserver !== "function") {
      // Ancient browser: hydrate everything through the pool right away.
      for (var i = 0; i < pending.length; i++) enqueueHydration(ctx, pending[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      if (ctx.root._odWlGen !== ctx.gen) return;
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        io.unobserve(entries[e].target);
        enqueueHydration(ctx, entries[e].target);
      }
    }, { rootMargin: "500px 0px 500px 0px" });
    ctx.root._odWlIO = io;
    for (var p = 0; p < pending.length; p++) io.observe(pending[p]);
  }

  // _odWlQueued on the element guards every entry path (observer, priority,
  // warm start, add-all) against double-hydration; the per-handle promise
  // cache makes any residual overlap free, and applyHydration's isConnected
  // check makes it harmless.
  function enqueueHydration(ctx, el) {
    if (el._odWlQueued) return;
    el._odWlQueued = true;
    ctx.queue.push(el);
    pumpHydration(ctx);
  }

  function pumpHydration(ctx) {
    while (ctx.active < HYDRATE_CONCURRENCY && ctx.queue.length) {
      var el = ctx.queue.shift();
      ctx.active += 1;
      hydrateElement(ctx, el).catch(function () {}).then(function () {
        ctx.active -= 1;
        if (ctx.root._odWlGen === ctx.gen) pumpHydration(ctx);
      });
    }
  }

  function hydrateElement(ctx, el) {
    var group = ctx.groups[Number(el.getAttribute("data-od-wl-group"))];
    if (!group) return Promise.resolve();
    return Promise.all([fetchProduct(group.handle), ctx.salePromise]).then(function (results) {
      if (ctx.root._odWlGen !== ctx.gen || !el.isConnected) return;
      applyHydration(ctx, el, group, results[0], results[1] || {});
    });
  }

  // Same classification ladder the blocking renderer used: live availability
  // decides, the stored row alone decides only when the fetch failed.
  function applyHydration(ctx, el, group, product, saleMap) {
    var saleHandles = saleMap[String((product && product.id) || group.latest.productId)] || "";
    var node;
    if (product && product.available && sellableVariants(product).length) {
      // Release qualification still derives from the very classification
      // that homes the card in Section A, so banner and list stay in step.
      var release = releaseFromGroup(group, product, ctx.dismissed);
      if (release) ctx.releases.push(release);
      node = replaceWithMarkup(el, availableCardMarkup(group, product, saleHandles, !!release));
      moveToSection(ctx, node, "avail");
      var selected = node.querySelector('[data-od-wl-format][aria-checked="true"]');
      if (selected) syncCardToSelection(node, selected);
    } else if (product) {
      node = replaceWithMarkup(el, notifyRowMarkup(group, product, null));
      moveToSection(ctx, node, "band");
    } else if (group.latest.itemType === "notify") {
      node = replaceWithMarkup(el, notifyRowMarkup(group, null, null));
      moveToSection(ctx, node, "band");
    } else {
      node = replaceWithMarkup(el, fallbackCardMarkup(group, saleHandles));
      moveToSection(ctx, node, "avail");
    }
    // Replacement and re-homing both defeat Alpine's mutation observer
    // (moved-in-batch nodes never bind); rebind this node's triggers.
    ensureTransitionTriggers(node);
  }

  function replaceWithMarkup(el, html) {
    var tpl = document.createElement("div");
    tpl.innerHTML = html;
    var node = tpl.firstChild;
    el.parentNode.replaceChild(node, el);
    return node;
  }

  // Re-homing: hydrated in place when the section already matches, else a
  // surgical appendChild into the other section's container (both shells
  // always exist). Counts/visibility refresh either way.
  function moveToSection(ctx, node, section) {
    var target = section === "avail"
      ? ctx.content.querySelector("[data-od-wl-grid]")
      : ctx.content.querySelector("[data-od-wl-rows]");
    if (target && node.parentNode !== target) target.appendChild(node);
    updateSectionCounts(ctx);
  }

  function updateSectionCounts(ctx) {
    var content = ctx.content;
    var grid = content.querySelector("[data-od-wl-grid]");
    var rows = content.querySelector("[data-od-wl-rows]");
    var availCount = grid ? grid.children.length : 0;
    var bandCount = rows ? rows.children.length : 0;
    if (grid) grid.hidden = !availCount;
    var aEmpty = content.querySelector("[data-od-wl-a-empty]");
    if (aEmpty) aEmpty.hidden = !!availCount;
    var addAll = content.querySelector("[data-od-wl-add-all]");
    if (addAll) addAll.hidden = !availCount;
    var availPill = content.querySelector("[data-od-wl-avail-pill]");
    if (availPill) availPill.textContent = pillText(availCount);
    var band = content.querySelector("[data-od-wl-band]");
    if (band) band.hidden = !bandCount;
    var bandPill = content.querySelector("[data-od-wl-band-pill]");
    if (bandPill) bandPill.textContent = pillText(bandCount);
  }

  // Release-banner candidates hydrate immediately (not viewport-gated);
  // when every candidate has settled, the banner renders in one go.
  function primeReleaseHydration(ctx) {
    var jobs = [];
    for (var i = 0; i < ctx.groups.length; i++) {
      if (!groupMayBeRelease(ctx.groups[i], ctx.dismissed)) continue;
      var el = ctx.content.querySelector('[data-od-wl-group="' + i + '"]');
      if (!el || el._odWlQueued) continue;
      el._odWlQueued = true;
      jobs.push(hydrateElement(ctx, el).catch(function () {}));
    }
    if (!jobs.length) return;
    Promise.all(jobs).then(function () {
      if (ctx.root._odWlGen !== ctx.gen) return;
      insertBanner(ctx);
    });
  }

  // The page below is already painted, so the banner is inserted above
  // Section A after the fact; if the user has scrolled past the insertion
  // point the scroll position is compensated so content does not jump.
  function insertBanner(ctx) {
    if (!ctx.releases.length || !ctx.content.isConnected) return;
    if (ctx.content.querySelector("[data-od-wl-release]")) return;
    var sectionA = ctx.content.querySelector(".od-wl-avail");
    if (!sectionA) return;
    var tpl = document.createElement("div");
    tpl.innerHTML = releaseBannerMarkup(ctx.releases);
    var wrap = tpl.firstChild;
    if (!wrap) return;
    sectionA.parentNode.insertBefore(wrap, sectionA);
    ensureTransitionTriggers(wrap);
    if (wrap.getBoundingClientRect().bottom <= 0) {
      window.scrollBy(0, wrap.offsetHeight);
    }
  }

  // Handles whose product record already settled hydrate NOW: these calls
  // resolve in microtasks (cached promise + session-cached sale map), which
  // run before the browser paints the innerHTML just set — so a re-render
  // shows finished cards immediately, indistinguishable from the old
  // blocking renderer.
  function warmStartCached(ctx) {
    var pending = ctx.content.querySelectorAll("[data-od-wl-pending]");
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el._odWlQueued) continue;
      var group = ctx.groups[Number(el.getAttribute("data-od-wl-group"))];
      if (!group || !Object.prototype.hasOwnProperty.call(productCacheValue, group.handle)) continue;
      el._odWlQueued = true;
      hydrateElement(ctx, el).catch(function () {});
    }
  }

  /* ---- page render ------------------------------------------------------ */

  function renderPage(root) {
    var content = root.querySelector("[data-od-wl-content]");
    if (!content) return;
    var gen = (root._odWlGen || 0) + 1;
    root._odWlGen = gen;
    teardownHydration(root);
    var store = getStore();
    if (!store || !store.ready) {
      content.innerHTML = skeletonMarkup();
      return;
    }
    var items = store.items || [];
    var browseUrl = root.getAttribute("data-browse-url") || "/collections/all";
    var loginUrl = root.getAttribute("data-login-url") || "/account/login";
    if (!items.length) {
      // Both sections empty: single centered empty state, no section headers.
      content.innerHTML = (store.loggedIn ? "" : guestBannerMarkup(loginUrl)) + emptyMarkup(browseUrl);
      ensureTransitionTriggers(content);
      return;
    }
    // Instant first paint from stored data: itemType decides the initial
    // section, hydration corrects any disagreement with live data later.
    var groups = groupItems(items);
    var availHtml = "";
    var availCount = 0;
    var notifyHtml = "";
    var notifyCount = 0;
    for (var i = 0; i < groups.length; i++) {
      if (isNotifyGroup(groups[i])) {
        notifyHtml += notifyRowMarkup(groups[i], null, i);
        notifyCount++;
      } else {
        availHtml += pendingCardMarkup(groups[i], i);
        availCount++;
      }
    }
    var html = "";
    if (!store.loggedIn) html += guestBannerMarkup(loginUrl);
    html += sectionAMarkup(availHtml, availCount, browseUrl);
    html += sectionBMarkup(notifyHtml, notifyCount, store.loggedIn, loginUrl);
    html += footerMarkup(browseUrl);
    content.innerHTML = html;
    // Covers all links in the fresh injection (cards, rows, footer); nodes
    // hydration replaces or moves afterwards get their own per-node rebind.
    // This also runs on the od:wishlist:changed / od:main-content-replaced /
    // SPA-return re-render paths, which all funnel through renderPage.
    ensureTransitionTriggers(content);
    var ctx = {
      root: root,
      content: content,
      gen: gen,
      groups: groups,
      dismissed: readDismissedReleases(),
      salePromise: loadSaleCollectionHandleMap().catch(function () { return {}; }),
      queue: [],
      active: 0,
      releases: []
    };
    root._odWlCtx = ctx;
    setupHydration(ctx);
    primeReleaseHydration(ctx);
    warmStartCached(ctx);
  }

  function bindPage(root) {
    if (root.dataset.odWlBound === "1") return;
    root.dataset.odWlBound = "1";
    root.addEventListener("click", function (event) {
      var formatButton = event.target.closest("[data-od-wl-format]");
      if (formatButton) {
        selectFormat(formatButton);
        return;
      }
      var dismissButton = event.target.closest("[data-od-wl-release-dismiss]");
      if (dismissButton) {
        // Dismiss ALL currently shown releases, then re-render (the banner
        // disappears and card flags drop; wishlist rows are untouched).
        var bannerEl = dismissButton.closest("[data-od-wl-release]");
        var releaseIds = bannerEl
          ? (bannerEl.getAttribute("data-od-wl-release-ids") || "").split(",").filter(Boolean)
          : [];
        if (releaseIds.length) dismissReleaseVariants(releaseIds);
        renderPage(root);
        return;
      }
      var shopReleasesButton = event.target.closest("[data-od-wl-release-shop]");
      if (shopReleasesButton) {
        var availGrid = root.querySelector(".od-wl-avail .od-wl-grid");
        if (availGrid) availGrid.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      var removeButton = event.target.closest("[data-od-wl-remove]");
      if (removeButton) {
        var removeCard = removeButton.closest("[data-od-wl-card]");
        if (removeCard) removeCardGroup(removeCard);
        return;
      }
      var addAllButton = event.target.closest("[data-od-wl-add-all]");
      if (addAllButton) {
        handleAddAll(root, addAllButton);
        return;
      }
      var cartButton = event.target.closest("[data-od-wl-cart]");
      if (cartButton) {
        var cartCard = cartButton.closest("[data-od-wl-card]");
        if (cartCard) handleCartClick(cartCard, cartButton);
      }
    });
    // Format radiogroup: roving tabindex + arrow-key selection.
    root.addEventListener("keydown", function (event) {
      var key = event.key;
      if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowDown") return;
      var current = event.target.closest && event.target.closest("[data-od-wl-format]");
      if (!current) return;
      var groupEl = current.closest('[role="radiogroup"]');
      if (!groupEl) return;
      event.preventDefault();
      var radios = Array.prototype.slice.call(groupEl.querySelectorAll("[data-od-wl-format]"));
      var index = radios.indexOf(current);
      var direction = key === "ArrowRight" || key === "ArrowDown" ? 1 : -1;
      var next = radios[(index + direction + radios.length) % radios.length];
      selectFormat(next);
      next.focus();
    });
  }

  function renderAllPages() {
    document.querySelectorAll("[data-od-wishlist-page]").forEach(function (root) {
      bindPage(root);
      renderPage(root);
    });
  }

  document.addEventListener("od:wishlist:changed", renderAllPages);
  document.addEventListener("shopify:section:load", renderAllPages);
  // app.js dispatches this after every SPA main-content morph.
  document.addEventListener("od:main-content-replaced", renderAllPages);

  // SPA swaps can insert the section after load; re-render when it appears.
  var pageObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var node = mutations[i].addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches("[data-od-wishlist-page]")) {
          bindPage(node);
          renderPage(node);
        } else if (node.querySelector && node.querySelector("[data-od-wishlist-page]")) {
          renderAllPages();
        }
      }
    }
  });

  function boot() {
    renderAllPages();
    pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* ------------------------------------------------------------------ */
  /* Cart-removal "save to wishlist?" toast                              */
  /*                                                                     */
  /* When a line item is fully removed from the cart (quantity hits 0 —  */
  /* not a decrease to >=1), offer to save that variant to the wishlist. */
  /* All interception happens here at runtime; app.js is not edited.     */
  /*                                                                     */
  /* Removal paths covered:                                              */
  /*   1. Alpine CartStore.updateCartItem(key, 0) — the funnel for       */
  /*      decreaseQty (drawer remove button), setQty (qty inputs on the  */
  /*      drawer AND the cart page reaching 0). Wrapped below.           */
  /*   2. [data-cart-remove] buttons (cart page rows) — handled by a     */
  /*      bubble-phase document click listener in theme.liquid that      */
  /*      POSTs {quantity:0} straight to /cart/change.js. We listen in   */
  /*      the CAPTURE phase so the line item is still in the Alpine cart */
  /*      store when we snapshot it.                                     */
  /*   3. window.Sunrise.updateQuantity(line, 0) — public helper in      */
  /*      app.js with no theme callers today; wrapped for completeness.  */
  /* Deliberately NOT covered (bulk flows, no prompt): the clear-cart    */
  /* confirm in theme.liquid (/cart/clear.js) and checkout — neither     */
  /* routes through the hooks above, and od:cart:cleared dismisses any   */
  /* visible toast.                                                      */
  /*                                                                     */
  /* Suppression: no prompt when the variant is already wishlisted       */
  /* (store.has), during ODWishlist.suppressCartPrompt(ms) windows       */
  /* (for any future wishlist-page code that removes cart lines), or     */
  /* for the same variantId twice within 1.5s (dedupe in case a removal  */
  /* ever flows through two hooks at once).                              */

  var TOAST_PROMPT_MS = 10000;
  var TOAST_SAVED_MS = 3000;
  var PROMPT_DEDUPE_MS = 1500;

  var toastEl = null;
  var toastTimer = null;
  var lastPromptAt = {}; // variantId -> epoch ms
  var suppressCartPromptUntil = 0;

  window.ODWishlist.suppressCartPrompt = function (ms) {
    var duration = isFinite(Number(ms)) ? Number(ms) : 3000;
    suppressCartPromptUntil = Date.now() + duration;
  };

  function cartStoreOf() {
    try {
      if (window.Alpine && window.Alpine.store) return window.Alpine.store("cart");
    } catch (e) {
      /* Alpine not ready */
    }
    return null;
  }

  /* Shopify /cart.js line-item shape (also what CartStore.cartItems holds,
     confirmed by cart-items.liquid bindings: item.product_title,
     item.variant_title, item.final_price, item.image, item.url):
     variant_id, product_id, handle, product_title, variant_title, sku,
     price (unit, cents), final_price (unit after discounts, cents),
     image (URL; processCartItems may rewrite it protocol-relative). */
  function wishlistItemFromCartItem(cartItem) {
    if (!cartItem || cartItem.variant_id == null) return null;
    var price = Number(cartItem.final_price);
    if (!isFinite(price)) price = Number(cartItem.price);
    var image = cartItem.image || "";
    if (image.indexOf("//") === 0) image = "https:" + image;
    return {
      variantId: String(cartItem.variant_id),
      productId: cartItem.product_id != null ? String(cartItem.product_id) : "",
      productHandle: cartItem.handle || "",
      productTitle: cartItem.product_title || "",
      variantTitle: cartItem.variant_title || "",
      sku: cartItem.sku || "",
      priceCentsAtAdd: isFinite(price) ? price : 0,
      imageUrl: image,
      source: "cart-save"
    };
  }

  function shouldPrompt(item) {
    if (!item) return false;
    var now = Date.now();
    if (now < suppressCartPromptUntil) return false;
    var store = getStore();
    if (store && typeof store.has === "function" && store.has(item.variantId)) return false;
    if (now - (lastPromptAt[item.variantId] || 0) < PROMPT_DEDUPE_MS) return false;
    lastPromptAt[item.variantId] = now;
    return true;
  }

  function maybePrompt(cartItem) {
    try {
      var item = wishlistItemFromCartItem(cartItem);
      if (shouldPrompt(item)) showRemovalToast(item);
    } catch (e) {
      warnOnce("cart-removal prompt failed", e);
    }
  }

  /* ---- toast UI ---- */

  function ensureToastEl() {
    if (toastEl && toastEl.isConnected) return toastEl;
    toastEl = document.createElement("div");
    toastEl.className = "od-wl-toast";
    toastEl.setAttribute("role", "status");
    toastEl.setAttribute("aria-live", "polite");
    toastEl.addEventListener("click", function (event) {
      if (event.target.closest("[data-od-wl-toast-close]")) hideToast();
    });
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function hideToast() {
    if (toastTimer) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }
    if (toastEl) toastEl.classList.remove("od-wl-toast--visible");
  }

  function closeButtonMarkup() {
    return '<button type="button" class="od-wl-toast__close" data-od-wl-toast-close aria-label="Dismiss">&times;</button>';
  }

  function showSavedToast() {
    var el = ensureToastEl();
    el.innerHTML = closeButtonMarkup() +
      '<p class="od-wl-toast__msg">Saved to wishlist ✓ ' +
      '<a class="od-wl-toast__link" href="/pages/wishlist" x-element-transition-trigger>View wishlist</a></p>';
    ensureTransitionTriggers(el);
    el.classList.add("od-wl-toast--visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_SAVED_MS);
  }

  function showRemovalToast(item) {
    var el = ensureToastEl();
    el.innerHTML = closeButtonMarkup() +
      '<p class="od-wl-toast__title">' + esc(item.productTitle || "Item") + "</p>" +
      '<p class="od-wl-toast__msg">Removed from cart. Save it to your wishlist?</p>' +
      '<div class="od-wl-toast__actions">' +
      '<button type="button" class="od-wl-btn od-wl-toast__save" data-od-wl-toast-save>Save to Wishlist</button>' +
      "</div>";
    var saveButton = el.querySelector("[data-od-wl-toast-save]");
    saveButton.addEventListener("click", function () {
      var store = getStore();
      if (!store || typeof store.add !== "function") {
        hideToast();
        return;
      }
      saveButton.disabled = true;
      // add() commits optimistically (and rolls itself back on API failure),
      // so the confirmation can show right away.
      store.add(item);
      showSavedToast();
    });
    el.classList.add("od-wl-toast--visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_PROMPT_MS);
  }

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    if (toastEl && toastEl.classList.contains("od-wl-toast--visible")) hideToast();
  });

  // Bulk flow: clearing the cart is not a single-line removal.
  document.addEventListener("od:cart:cleared", hideToast);

  /* ---- interception ---- */

  // Path 1: Alpine cart store funnel. decreaseQty/setQty look the item up
  // and call updateCartItem(key, qty); qty === 0 means full removal. The
  // item object is still present in cartItems here, so snapshot it first.
  var cartStoreWrapped = false;
  function wrapCartStore() {
    if (cartStoreWrapped) return true;
    var cartStore = cartStoreOf();
    if (!cartStore || typeof cartStore.updateCartItem !== "function") return false;
    var original = cartStore.updateCartItem;
    cartStore.updateCartItem = function (key, qty) {
      try {
        if (Number(qty) === 0) {
          var cartItem = (this.cartItems || []).find(function (item) {
            return item.key === key;
          });
          maybePrompt(cartItem);
        }
      } catch (e) {
        /* never block the removal */
      }
      return original.apply(this, arguments);
    };
    cartStoreWrapped = true;
    return true;
  }

  // Path 3: window.Sunrise.updateQuantity(line, qty) — line is 1-based.
  function wrapSunrise() {
    var sunrise = window.Sunrise;
    if (!sunrise || typeof sunrise.updateQuantity !== "function") return false;
    if (sunrise.__odWlWrapped) return true;
    var original = sunrise.updateQuantity;
    sunrise.updateQuantity = function (line, qty) {
      try {
        if (Number(qty) === 0) {
          var cartStore = cartStoreOf();
          var items = cartStore && cartStore.cartItems;
          maybePrompt(items ? items[Number(line) - 1] : null);
        }
      } catch (e) {
        /* never block the removal */
      }
      return original.apply(this, arguments);
    };
    sunrise.__odWlWrapped = true;
    return true;
  }

  // Path 2: cart-page [data-cart-remove] buttons. theme.liquid handles these
  // with a bubble-phase document listener that always removes the whole line
  // ({quantity: 0}); capture phase runs first, while the Alpine cart store
  // (hydrated from cart.liquid's data-od-cart-json) still has the item.
  document.addEventListener("click", function (event) {
    var button = event.target && event.target.closest && event.target.closest("[data-cart-remove]");
    if (!button) return;
    try {
      var cartStore = cartStoreOf();
      var items = cartStore && cartStore.cartItems;
      if (!items || !items.length) return;
      var key = button.getAttribute("data-key");
      var line = Number(button.getAttribute("data-line") || 0);
      var cartItem = null;
      if (key) {
        cartItem = items.find(function (item) {
          return item.key === key;
        });
      }
      if (!cartItem && line > 0) cartItem = items[line - 1];
      maybePrompt(cartItem);
    } catch (e) {
      /* never block the removal */
    }
  }, true);

  // The cart store registers on alpine:init (app.js) and window.Sunrise is
  // assigned at the end of app.js; retry until both wraps land.
  function tryWrapAll() {
    return wrapCartStore() && wrapSunrise();
  }
  if (!tryWrapAll()) {
    document.addEventListener("alpine:initialized", tryWrapAll);
    var wrapAttempts = 0;
    var wrapInterval = setInterval(function () {
      wrapAttempts += 1;
      if (tryWrapAll() || wrapAttempts >= 40) clearInterval(wrapInterval);
    }, 500);
  }
})();
